/* Epic — hero slider. Autoplay with pause on hover and focus, reduced-motion
   aware, and it stops entirely when the section scrolls out of view so a
   background tab is not animating for nobody. */
(function () {
  'use strict';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('.epic-slide'));
    if (slides.length < 2) return;

    /* Build the tab bar from each slide's own <template>. The section cannot
       read a theme block's settings, so the slide renders its tab and this
       moves it into place — once, so a theme-editor re-init does not double
       it. The bar is hidden until this has run (data-ready), so a page whose
       script never arrives shows no dead controls. */
    var tabsBox = root.querySelector('[data-hero-tabs]');
    if (tabsBox && !tabsBox.children.length) {
      slides.forEach(function (sl) {
        var tpl = sl.querySelector('template.epic-slide__tabtpl');
        if (tpl) tabsBox.appendChild(tpl.content.cloneNode(true));
      });
    }
    var bar = root.querySelector('.epic-hero__bar');
    if (bar) bar.setAttribute('data-ready', '');

    var dots  = Array.prototype.slice.call(root.querySelectorAll('.epic-hero__tab'));
    var delay = parseInt(root.getAttribute('data-interval'), 10) || 6500;
    var i = 0, timer = null, paused = false, visible = true;

    // The dot fill counts down the same interval that advances the slide, so
    // the two cannot drift apart.
    root.style.setProperty('--epic-dot-dur', (delay / 1000) + 's');

    // Re-running an animation needs the old one taken off and a reflow read
    // between, or the browser coalesces the two styles and nothing restarts.
    function restartFill() {
      var bar = dots[i] && dots[i].querySelector('.epic-hero__pg b');
      if (!bar) return;
      bar.style.animation = 'none';
      void bar.offsetWidth;
      bar.style.animation = '';
    }

    function setPaused(v) {
      paused = v;
      root.setAttribute('data-paused', String(v));
    }

    function show(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach(function (s, x) { s.setAttribute('data-active', String(x === i)); });
      dots.forEach(function (d, x) { d.setAttribute('aria-selected', String(x === i)); });
      restartFill();
      root.dispatchEvent(new CustomEvent('epic:slide', { detail: { index: i } }));
    }
    function tick()  { show(i + 1); }
    /* The progress bar advances the carousel, not a parallel setInterval.
       The old pair — a timer AND a fill animation of the same length — could
       drift apart under a backgrounded tab or a paused animation, and the
       bar would finish against a slide that had already changed, or sit full
       while nothing happened. One source of truth: when the bar finishes,
       the slide moves. The interval below is only a safety net for a browser
       that never fires animationend. */
    function start() {
      if (reduce || paused || !visible) return;
      stop();
      timer = setInterval(tick, delay + 400);
      restartFill();
    }
    function stop()  { if (timer) { clearInterval(timer); timer = null; } }

    root.addEventListener('mouseenter', function () { setPaused(true);  stop();  });
    root.addEventListener('mouseleave', function () { setPaused(false); start(); });
    root.addEventListener('focusin',    function () { setPaused(true);  stop();  });
    root.addEventListener('focusout',   function () { setPaused(false); start(); });

    dots.forEach(function (d, x) {
      d.addEventListener('click', function () { show(x); start(); });
      var bar = d.querySelector('.epic-hero__pg b');
      if (bar) {
        bar.addEventListener('animationend', function () {
          // Only the slide that is actually up gets to advance the carousel.
          if (!reduce && !paused && visible && x === i) { show(i + 1); start(); }
        });
      }
    });
    var prev = root.querySelector('.epic-hero__nav--prev');
    var next = root.querySelector('.epic-hero__nav--next');
    if (prev) prev.addEventListener('click', function () { show(i - 1); start(); });
    if (next) next.addEventListener('click', function () { show(i + 1); start(); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        root.setAttribute('data-paused', String(!visible || paused));
        if (visible) start(); else stop();
      }, { threshold: 0.15 }).observe(root);
    }

    /* ── arrow keys ──────────────────────────────────────────────────────
       Listened for on the root, so they work whenever focus is anywhere
       inside the carousel — the dots and the prev/next buttons are already
       focusable, and the track carries tabindex="0" so a keyboard user can
       reach the slide itself. Home and End are in the WAI-ARIA carousel
       pattern and cost two lines. */
    root.addEventListener('keydown', function (e) {
      var k = e.key, handled = true;
      if (k === 'ArrowRight') show(i + 1);
      else if (k === 'ArrowLeft') show(i - 1);
      else if (k === 'Home') show(0);
      else if (k === 'End') show(slides.length - 1);
      else handled = false;
      if (handled) { e.preventDefault(); start(); }
    });

    /* ── drag and swipe ──────────────────────────────────────────────────
       The slides are cross-faded, not a track, so there is nothing to slide.
       What moves under the finger is the ACTIVE slide, by the drag distance
       with a damping factor, through a custom property the stylesheet reads.
       That gives the gesture something to answer rather than pretending the
       layout is a filmstrip it is not.

       Three things this has to get right:

       - Vertical scrolling stays native. The track is touch-action: pan-y,
         and a gesture is only claimed once it is both past 8px and more
         horizontal than vertical. A carousel that eats downward swipes on a
         phone is worse than one with no gesture at all.
       - A drag must not fire the link it started on. Any movement past the
         threshold sets a flag that swallows the next click in the capture
         phase, so the CTA inside a slide still works on a tap and does not
         fire when someone swipes across it.
       - Autoplay is suspended for the whole gesture and restarted after,
         through the same paused flag the hover and focus handlers use, so
         the three cannot disagree about whether the timer should run. */
    var track = root.querySelector('.epic-hero__track');
    if (track && window.PointerEvent) {
      var dragging = false, claimed = false, startX = 0, startY = 0, dx = 0, pid = null;
      var DAMP = 0.42;          // the slide trails the finger rather than matching it
      var CLAIM = 8;            // px before a gesture counts as horizontal
      var THRESHOLD = 54;       // px of real travel before the slide changes

      function setDrag(v) {
        root.style.setProperty('--epic-drag', v.toFixed(1) + 'px');
      }
      function endDrag(commit) {
        if (!dragging) return;
        dragging = false;
        root.removeAttribute('data-dragging');
        setDrag(0);
        if (commit && Math.abs(dx) > THRESHOLD) show(dx < 0 ? i + 1 : i - 1);
        setPaused(false);
        start();
      }

      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        pid = e.pointerId;
        startX = e.clientX; startY = e.clientY; dx = 0;
        dragging = false; claimed = false;
      });

      track.addEventListener('pointermove', function (e) {
        if (e.pointerId !== pid) return;
        var mx = e.clientX - startX, my = e.clientY - startY;
        if (!claimed) {
          if (Math.abs(mx) < CLAIM && Math.abs(my) < CLAIM) return;
          // The first decisive movement decides whose gesture this is, once.
          if (Math.abs(mx) <= Math.abs(my)) { pid = null; return; }
          claimed = true; dragging = true;
          setPaused(true); stop();
          root.setAttribute('data-dragging', 'true');
          if (track.setPointerCapture) { try { track.setPointerCapture(pid); } catch (err) {} }
        }
        dx = mx;
        setDrag(dx * DAMP);
      });

      track.addEventListener('pointerup', function (e) {
        if (e.pointerId !== pid) return;
        var moved = dragging && Math.abs(dx) > CLAIM;
        endDrag(true);
        pid = null;
        if (moved) {
          // Capture phase, once: the click that follows this pointerup is the
          // browser finishing the gesture, not the user choosing a link.
          track.addEventListener('click', function swallow(ev) {
            ev.preventDefault(); ev.stopPropagation();
            track.removeEventListener('click', swallow, true);
          }, true);
        }
      });

      track.addEventListener('pointercancel', function () { endDrag(false); pid = null; });
      // A pointer that leaves the window never reports up; treat it as cancel.
      window.addEventListener('blur', function () { endDrag(false); pid = null; });
    }

    /* ── callout pins ────────────────────────────────────────────────────
       Hover and focus are handled in CSS. This exists for touch, where
       neither happens: a tap toggles aria-expanded, which the stylesheet
       keys off, and opening one closes the rest so two bubbles never overlap
       on a small plate. Escape closes, because a tooltip that can only be
       dismissed by finding somewhere else to tap is a trap. */
    var pins = Array.prototype.slice.call(root.querySelectorAll('.epic-hot__dot'));
    if (pins.length) {
      var closeAll = function (except) {
        pins.forEach(function (d) {
          if (d !== except) d.setAttribute('aria-expanded', 'false');
        });
      };
      pins.forEach(function (d) {
        d.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var open = d.getAttribute('aria-expanded') === 'true';
          closeAll(d);
          d.setAttribute('aria-expanded', open ? 'false' : 'true');
        });
      });
      /* On the document, not the root: a bubble opened by a tap leaves focus
         wherever the tap left it, and a keydown listener on the carousel
         never hears the Escape that follows. Found by a test that tapped a
         pin and pressed Escape, and watched the bubble stay open. */
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAll(null);
      });
      // A slide change takes its bubbles with it.
      root.addEventListener('epic:slide', function () { closeAll(null); });
    }

    /* ── chips that cue their pin ────────────────────────────────────────
       A chip carrying data-feat-pin names a callout on the same slide, by
       position. Hovering or focusing the chip opens that pin's bubble and
       marks the dot, so the eye can travel from the word to the place on the
       picture it describes. The two were describing the same fabric from
       opposite sides of the slide with no idea the other existed.

       The pin lookup is per slide, not per section: pin 2 means the second
       callout on THIS slide. Looked up at hover rather than cached, because
       a section reload in the theme editor replaces the nodes underneath. */
    var cues = Array.prototype.slice.call(root.querySelectorAll('[data-feat-pin]'));
    cues.forEach(function (chip) {
      var slide = chip.closest ? chip.closest('.epic-slide') : null;
      if (!slide) return;

      var dotFor = function () {
        var n = parseInt(chip.getAttribute('data-feat-pin'), 10);
        if (!(n >= 1)) return null;
        return slide.querySelectorAll('.epic-hot__dot')[n - 1] || null;
      };
      var cue = function (on) {
        var d = dotFor();
        if (!d) return;
        d.classList.toggle('is-cued', on);
        d.setAttribute('aria-expanded', on ? 'true' : 'false');
      };

      chip.addEventListener('mouseenter', function () { cue(true); });
      chip.addEventListener('mouseleave', function () { cue(false); });
      chip.addEventListener('focus', function () { cue(true); });
      chip.addEventListener('blur', function () { cue(false); });
    });

    // Chips that explain themselves rather than linking: tap toggles, for
    // touch, where there is no hover to rely on.
    Array.prototype.slice.call(root.querySelectorAll('button.epic-slide__featbtn'))
      .forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        });
      });

    /* ── pointer parallax ────────────────────────────────────────────────
       --mx and --my are the cursor across the hero from -1 to 1, eased
       toward the target at 8% a frame rather than snapped, because the
       snapped version reads as the picture being dragged rather than as
       depth. --sy is the scroll offset, written on its own rAF.

       Only on a device with a real pointer, and never under reduced motion:
       a phone pays nothing for this, and neither does anyone who asked not
       to be moved. */
    if (!reduce && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var M = { x: 0, y: 0, tx: 0, ty: 0, raf: 0 };
      var frame = function () {
        M.raf = 0;
        M.x += (M.tx - M.x) * 0.08;
        M.y += (M.ty - M.y) * 0.08;
        root.style.setProperty('--mx', M.x.toFixed(4));
        root.style.setProperty('--my', M.y.toFixed(4));
        if (Math.abs(M.tx - M.x) > 0.001 || Math.abs(M.ty - M.y) > 0.001) {
          M.raf = requestAnimationFrame(frame);
        }
      };
      var kick = function () { if (!M.raf) M.raf = requestAnimationFrame(frame); };

      root.addEventListener('pointermove', function (e) {
        if (root.getAttribute('data-dragging') === 'true') return;
        var r = root.getBoundingClientRect();
        M.tx = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width - 0.5) * 2));
        M.ty = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height - 0.5) * 2));
        kick();
      });
      root.addEventListener('pointerleave', function () { M.tx = 0; M.ty = 0; kick(); });

      var sraf = 0;
      window.addEventListener('scroll', function () {
        if (sraf) return;
        sraf = requestAnimationFrame(function () {
          sraf = 0;
          root.style.setProperty('--sy', Math.min(window.scrollY, root.offsetHeight).toFixed(1));
        });
      }, { passive: true });
    }

    show(0);
    start();
    root.addEventListener('shopify:section:unload', stop);
  }

  function boot() { document.querySelectorAll('[data-epic-hero]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
